// ============================================================================
// plot/projection.ts — projection coords MATH → px d'un viewBox SVG logique.
// PUR & testable. Y-flip (math vers le haut → écran vers le bas). Réutilisé par
// les renderers function_graph et vector_diagram (SVG pur, sans JSXGraph).
// ============================================================================

export interface PlotViewport {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface PlotView {
  /** Dimensions du viewBox logique (px). */
  w: number;
  h: number;
  /** math x → px. */
  tx: (mx: number) => number;
  /** math y → px (Y-flip). */
  ty: (my: number) => number;
  /** longueur math → px, horizontale / verticale (égales si isotrope). */
  sx: (len: number) => number;
  sy: (len: number) => number;
}

export interface PlotViewOptions {
  /**
   * `true` : échelle unique X=Y, figure centrée (géométrie/vecteurs — angles et
   * cercles préservés). `false` (défaut) : échelles indépendantes remplissant la
   * boîte (graphes de fonction — équivaut à la boundingbox JSXGraph).
   */
  isotropic?: boolean;
  /** Marge intérieure en px (défaut 0), la même partout ou par côté. */
  pad?: number | { left: number; right: number; top: number; bottom: number };
}

export function makePlotView(
  vp: PlotViewport,
  size: { w: number; h: number },
  opts: PlotViewOptions = {},
): PlotView {
  const p = opts.pad ?? 0;
  const pad = typeof p === 'number' ? { left: p, right: p, top: p, bottom: p } : p;
  const { w, h } = size;
  const rangeX = Math.max(vp.xMax - vp.xMin, 1e-9);
  const rangeY = Math.max(vp.yMax - vp.yMin, 1e-9);
  const availW = Math.max(w - pad.left - pad.right, 1e-9);
  const availH = Math.max(h - pad.top - pad.bottom, 1e-9);

  let scaleX = availW / rangeX;
  let scaleY = availH / rangeY;
  let offX = pad.left;
  let offY = pad.top;

  if (opts.isotropic) {
    const s = Math.min(scaleX, scaleY);
    scaleX = s;
    scaleY = s;
    // centre la figure dans l'espace disponible
    offX = (w - rangeX * s) / 2;
    offY = (h - rangeY * s) / 2;
  }

  return {
    w,
    h,
    tx: (mx) => offX + (mx - vp.xMin) * scaleX,
    ty: (my) => offY + (vp.yMax - my) * scaleY, // flip
    sx: (len) => len * scaleX,
    sy: (len) => len * scaleY,
  };
}
